import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Badge } from "@/components/ui/badge";
import { Shield, ExternalLink, AlertTriangle } from "lucide-react";
import type { PageIssue, UnsafeCrossoriginDetails } from "@/types/technicalAudit";

interface UnsafeCrossoriginCardProps {
	issue: PageIssue;
}

export function UnsafeCrossoriginCard({ issue }: UnsafeCrossoriginCardProps) {
	const details = issue.issue_details as UnsafeCrossoriginDetails;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Unsafe Cross-Origin Link</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						This link opens in a new tab without <code className="px-1 py-0.5 bg-red-100 dark:bg-red-900/40 rounded text-xs">rel="noopener"</code>. This exposes your site to <code className="px-1 py-0.5 bg-red-100 dark:bg-red-900/40 rounded text-xs">window.opener</code> attacks.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Link Details
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div>
						<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Source Page</p>
						<a
							href={details.page_url}
							onClick={(e) => openWithCacheBuster(e, details.page_url)}
							target="_blank"
							rel="noopener noreferrer"
							className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
						>
							<p className="text-sm font-mono break-all text-foreground flex-1">{details.page_url}</p>
							<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
						</a>
					</div>
					<div>
						<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Destination</p>
						<div className="p-3 rounded-lg border bg-orange-50 dark:bg-orange-950/40/50 border-orange-200 dark:border-orange-800">
							<p className="text-sm font-mono break-all text-orange-700 dark:text-orange-400">{details.target_url}</p>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<span className="text-xs text-muted-foreground">Current rel:</span>
						<Badge variant="outline" className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800">
							{details.rel_attributes || "none"}
						</Badge>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
